import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bestBeforeLevel, daysUntil, describeDaysLeft, effectiveBestBefore } from "@logiful/domain";
import { CalendarClock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { LEVEL } from "@/pages/BestBefore";
import { api, canManage, type ProductItem } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Button, cx, Input } from "./ui";
import { intlLocale, t } from "@/lib/i18n";

/**
 * Chargen & MHD im Artikel: Chargen mit Restlaufzeit (auch ohne MHD, auch gesperrte Mengen) + optionale eigene Schwellen.
 * Sichtbar, sobald das Lager Chargen meldet – auch wenn das Kennzeichen nur in FFN gesetzt ist.
 */
export function BestBeforeSection({ p, onSaved }: { p: ProductItem; onSaved: () => void }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const defaults = useQuery({ queryKey: ["best-before", tenantId], queryFn: () => api.stock.bestBefore(tenantId), select: (d) => d.settings });
  const own = p.bestBefore;
  const [custom, setCustom] = useState(own?.warningDays != null || own?.criticalDays != null);
  const [w, setW] = useState(own?.warningDays != null ? String(own.warningDays) : "");
  const [c, setC] = useState(own?.criticalDays != null ? String(own.criticalDays) : "");
  const [saving, setSaving] = useState(false);

  const lots = own?.lots ?? [];
  const showThresholds = p.handling.bestBeforeManaged || lots.some((l) => l.bestBefore);
  if ((!p.handling.bestBeforeManaged && !p.handling.batchManaged && !lots.length) || !defaults.data) return null;
  const s = effectiveBestBefore(defaults.data, custom ? { warningDays: w ? Number(w) : null, criticalDays: c ? Number(c) : null } : undefined);
  const valid = !custom || (s.criticalDays < s.warningDays && s.warningDays >= 1);
  const dirty = custom !== (own?.warningDays != null || own?.criticalDays != null) || (custom && (w !== String(own?.warningDays ?? "") || c !== String(own?.criticalDays ?? "")));
  const now = new Date();

  const save = async () => {
    setSaving(true);
    try {
      await api.products.patch(tenantId, p.id, custom ? { bestBeforeWarningDays: w ? Number(w) : null, bestBeforeCriticalDays: c ? Number(c) : null } : { bestBeforeWarningDays: null, bestBeforeCriticalDays: null });
      toast.success(custom ? "Eigene MHD-Schwellen gespeichert" : "Standard-Schwellen des Kunden aktiv");
      void qc.invalidateQueries({ queryKey: ["best-before", tenantId] });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="flex items-baseline mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5"><CalendarClock className="size-3.5" /> {showThresholds ? t("Chargen & Mindesthaltbarkeit") : t("Chargen")}</h3>
        {showThresholds && <span className="ml-auto text-[12px] text-fg-subtle">{t("Warnung ≤")} {s.warningDays} {t("T. · kritisch ≤")} {s.criticalDays} T.</span>}
      </div>

      {lots.length ? (
        <ul className="rounded-xl border border-border divide-y divide-border mb-3">
          {lots.map((l, i) => {
            const days = l.bestBefore ? daysUntil(l.bestBefore, now) : null;
            const lv = days != null ? bestBeforeLevel(days, s) : null;
            return (
              <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px]">
                <span className="font-mono text-[12px] min-w-20 truncate" title={t("Charge")}>{l.batch ?? "—"}</span>
                <span className="tabular-nums w-24">{l.bestBefore ? new Date(l.bestBefore).toLocaleDateString(intlLocale()) : <span className="text-fg-subtle">{t("ohne MHD")}</span>}</span>
                {lv && days != null && <span className={cx("inline-flex items-center gap-1.5 h-6 px-2 rounded-md border text-[12px] font-medium", LEVEL[lv].bg, LEVEL[lv].text)}>{describeDaysLeft(days)}</span>}
                <span className="ml-auto tabular-nums text-fg-muted">
                  {fmtNumber(l.quantity)} {t("Stk.")}{l.blocked > 0 && <span className="ml-1.5 text-warning" title={t("im Lager gesperrt, z. B. Wareneingangsprüfung")}>+ {fmtNumber(l.blocked)} {t("gesperrt")}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mb-3 text-[12.5px] text-fg-muted">{t("Aktuell keine Chargen im Lager.")}</p>
      )}

      {!p.handling.bestBeforeManaged && !p.handling.batchManaged && lots.length > 0 && (
        <p className="mb-3 text-[12px] text-fg-muted">{t("Das Lager führt diesen Artikel mit Chargen")}{lots.some((l) => l.bestBefore) ? t("/MHD") : ""}{t(", im Portal ist er noch nicht so gekennzeichnet – unter „Lagerkennzeichen“ ergänzen, damit es auch bei Prüfungen berücksichtigt wird.")}</p>
      )}

      {canManage(role) && showThresholds && (
        <div className="rounded-xl border border-border p-3">
          <button role="switch" aria-checked={custom} onClick={() => setCustom(!custom)} className="flex items-center gap-2.5">
            <span className={cx("w-8 h-[18px] rounded-full p-0.5 transition-colors", custom ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", custom && "translate-x-3.5")} /></span>
            <span className="text-[13px]">{t("Eigene Schwellen für diesen Artikel")} <span className="text-fg-subtle">{t("(Standard:")} {defaults.data.warningDays} / {defaults.data.criticalDays} {t("Tage)")}</span></span>
          </button>
          {custom && (
            <div className="mt-3 grid grid-cols-2 gap-3 animate-fade-in">
              <Input type="number" min={1} value={w} onChange={(e) => setW(e.target.value)} placeholder={String(defaults.data.warningDays)} suffix={t("T. Warnung")} invalid={!valid} />
              <Input type="number" min={0} value={c} onChange={(e) => setC(e.target.value)} placeholder={String(defaults.data.criticalDays)} suffix={t("T. kritisch")} invalid={!valid} />
            </div>
          )}
          {!valid && <p className="mt-2 text-[12px] text-danger">{t("„Kritisch“ muss kleiner als „Warnung“ sein.")}</p>}
          {dirty && (
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="primary" disabled={!valid} loading={saving} onClick={() => void save()}>{t("Schwellen speichern")}</Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
